import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button, Spinner, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useRoleContext, ROLE_PM } from "../../context/RoleContext";
import { useAuthUser } from "../../hooks/AuthProvider";
import useMyRentals from "../../hooks/useMyRentals";
import {
  getPropertyById,
  onPendingInvitations,
  acceptInvitation,
  declineInvitation,
  getInspectionsByProperty,
} from "../../helpers/firestoreService";

const TenantHome = () => {
  document.title = "Tenant Home | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();
  const { tenantRecords, setActiveRole, refreshRoles } = useRoleContext();
  const { rentals, loading: rentalsLoading, reload: reloadRentals } = useMyRentals();

  const [invitations, setInvitations] = useState([]); // [{ ...invitation, property }]
  const [invActionLoading, setInvActionLoading] = useState({});
  const [openInspections, setOpenInspections] = useState([]); // [{ ...inspection, tenantRecord, property }]

  // Open inspections on any rental the tenant has already accepted — stays
  // visible until the PM marks it closed, same idea as Pending Invitations.
  useEffect(() => {
    if (rentalsLoading || rentals.length === 0) { setOpenInspections([]); return; }
    let cancelled = false;
    (async () => {
      const perRental = await Promise.all(
        rentals.map(({ tenantRecord, property }) =>
          getInspectionsByProperty(tenantRecord.propertyId)
            .then((rows) => rows.filter((i) => i.status !== "closed").map((i) => ({ ...i, tenantRecord, property })))
            .catch(() => [])
        )
      );
      if (!cancelled) setOpenInspections(perRental.flat());
    })();
    return () => { cancelled = true; };
  }, [rentals, rentalsLoading]);

  // Live pending invitations for this email — same source TenantMyRentals uses.
  useEffect(() => {
    if (!user?.email) return;
    const unsubscribe = onPendingInvitations(user.email, async (invs) => {
      const withProperty = await Promise.all(
        invs.map(async (inv) => ({
          ...inv,
          property: inv.propertyId ? await getPropertyById(inv.propertyId).catch(() => null) : null,
        }))
      );
      setInvitations(withProperty);
    });
    return () => unsubscribe();
  }, [user?.email]);

  const handleAccept = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "accept" }));
    try {
      await acceptInvitation(inv.id, user.uid, user.displayName || "");
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      await refreshRoles();
      await reloadRentals();
    } catch (e) {
      console.error("Accept invitation error:", e);
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n; });
    }
  };

  const handleDecline = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "decline" }));
    try {
      await declineInvitation(inv.id);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
    } catch (e) {
      console.error("Decline invitation error:", e);
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n; });
    }
  };

  const hasTenantRecord = tenantRecords && tenantRecords.length > 0;
  const firstName = user?.displayName?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: 860 }}>

        {/* Header */}
        <div className="mb-4">
          <h4 className="fw-bold mb-1">Welcome back, {firstName}</h4>
          <p className="text-muted mb-0">Here's a quick overview of your renter account.</p>
        </div>

        {/* Pending invitations — surfaced immediately on login, not just under My Rentals */}
        {invitations.length > 0 && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <h6 className="fw-bold mb-3">
                <i className="bx bx-envelope text-warning me-2" />
                Pending Invitations
                <Badge color="warning" pill className="ms-2">{invitations.length}</Badge>
              </h6>
              {invitations.map((inv) => (
                <div
                  key={inv.id}
                  className="d-flex align-items-center justify-content-between gap-3 rounded p-3 mb-2"
                  style={{ background: "#fffbeb" }}
                >
                  <div className="min-w-0">
                    <div className="fw-semibold text-truncate">
                      {inv.property?.address || "A property"}
                    </div>
                    <div className="text-muted small">
                      You&apos;ve been invited as a tenant.
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      color="success"
                      disabled={!!invActionLoading[inv.id]}
                      onClick={() => handleAccept(inv)}
                    >
                      {invActionLoading[inv.id] === "accept" ? <Spinner size="sm" /> : "Accept"}
                    </Button>
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      disabled={!!invActionLoading[inv.id]}
                      onClick={() => handleDecline(inv)}
                    >
                      {invActionLoading[inv.id] === "decline" ? <Spinner size="sm" /> : "Decline"}
                    </Button>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {/* Open inspections — stay visible until the PM marks them complete */}
        {openInspections.length > 0 && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <h6 className="fw-bold mb-3">
                <i className="bx bx-clipboard text-primary me-2" />
                Open Inspections
                <Badge color="primary" pill className="ms-2">{openInspections.length}</Badge>
              </h6>
              {openInspections.map((insp) => (
                <div
                  key={insp.id}
                  className="d-flex align-items-center justify-content-between gap-3 rounded p-3 mb-2"
                  style={{ background: "#eff2ff" }}
                >
                  <div className="min-w-0">
                    <div className="fw-semibold text-truncate">{insp.title || "Inspection"}</div>
                    <div className="text-muted small text-truncate">
                      {insp.property?.address || "A property"}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    color="primary"
                    className="flex-shrink-0"
                    onClick={() => navigate(`/tenant/my-rentals/${insp.tenantRecord.id}?tab=inspections`)}
                  >
                    View Inspection
                  </Button>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {/* Your rentals — front and center so paying rent is one click away */}
        {rentalsLoading ? (
          <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>
        ) : rentals.length > 0 && (
          <div className="mb-4">
            <h6 className="fw-bold mb-3">Your Rentals</h6>
            <Row className="g-3">
              {rentals.map(({ tenantRecord, property }) => (
                <Col xs={12} md={6} key={tenantRecord.id}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="d-flex flex-column p-4">
                      <div className="d-flex align-items-start gap-3 mb-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 44, height: 44, background: "#eff2ff" }}
                        >
                          <i className="bx bx-building-house text-primary" style={{ fontSize: "1.3rem" }} />
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <h6 className="fw-bold mb-0 text-truncate">{property?.address || "Loading address…"}</h6>
                          {property?.city && (
                            <div className="text-muted small">
                              <i className="bx bx-map me-1" />{property.city}{property.state ? `, ${property.state}` : ""}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-auto">
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => navigate(`/tenant/my-rentals/${tenantRecord.id}?tab=rent`)}
                        >
                          <i className="bx bx-credit-card me-1" />Pay Rent
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          outline
                          onClick={() => navigate(`/tenant/my-rentals/${tenantRecord.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Quick-action cards */}
        <Row className="g-3 mb-4">

          <Col xs={12} sm={6} md={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex flex-column align-items-start p-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{ width: 48, height: 48, background: "#eff2ff" }}
                >
                  <i className="bx bx-search-alt" style={{ fontSize: "1.4rem", color: "#556ee6" }} />
                </div>
                <h6 className="fw-bold mb-1">Rental Applications</h6>
                <p className="text-muted small mb-3 flex-grow-1">Track your applications and continue the onboarding process.</p>
                <Button color="primary" size="sm" outline onClick={() => navigate("/my-applications")}>
                  Open
                </Button>
              </CardBody>
            </Card>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex flex-column align-items-start p-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{ width: 48, height: 48, background: "#f0faf4" }}
                >
                  <i className="bx bx-file" style={{ fontSize: "1.4rem", color: "#34c38f" }} />
                </div>
                <h6 className="fw-bold mb-1">My Applications</h6>
                <p className="text-muted small mb-3 flex-grow-1">Track the status of your rental applications.</p>
                <Button color="success" size="sm" outline onClick={() => navigate("/my-applications")}>
                  View
                </Button>
              </CardBody>
            </Card>
          </Col>

          {hasTenantRecord && rentals.length === 0 && (
            <Col xs={12} sm={6} md={4}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="d-flex flex-column align-items-start p-4">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                    style={{ width: 48, height: 48, background: "#fff8ec" }}
                  >
                    <i className="bx bx-buildings" style={{ fontSize: "1.4rem", color: "#f1b44c" }} />
                  </div>
                  <h6 className="fw-bold mb-1">My Rentals</h6>
                  <p className="text-muted small mb-3 flex-grow-1">Accept a pending invitation to see your rental here.</p>
                  <Button color="warning" size="sm" outline onClick={() => navigate("/tenant/my-rentals")}>
                    View Rentals
                  </Button>
                </CardBody>
              </Card>
            </Col>
          )}

          <Col xs={12} sm={6} md={hasTenantRecord && rentals.length === 0 ? 12 : 4}>
            <Card className="border-0 shadow-sm h-100" style={{ borderStyle: "dashed", borderColor: "#dee2e6" }}>
              <CardBody className="d-flex flex-column align-items-start p-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{ width: 48, height: 48, background: "#f7f0ff" }}
                >
                  <i className="bx bx-building" style={{ fontSize: "1.4rem", color: "#7b5ea7" }} />
                </div>
                <h6 className="fw-bold mb-1">Manage Properties?</h6>
                <p className="text-muted small mb-3 flex-grow-1">Switch into Property Manager mode to list and manage your own rentals.</p>
                <Button color="secondary" size="sm" outline onClick={() => { setActiveRole(ROLE_PM); navigate("/overview"); }}>
                  Get Started
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default TenantHome;
